import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Search, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { FallingMapleLeaves } from '@/components/shared/FallingMapleLeaves';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = ['All', 'ESG Advisory', 'Carbon Markets', 'Climate Finance', 'Supply Chain', 'Digital Solutions'];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        await axios.post(`${API}/blog/seed`);
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [setPosts, setLoading]);

  const filteredPosts = posts.filter(post => {
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <main data-testid="blog-page" className="pt-20">
      {/* Hero Section - Maple Theme */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 -top-20">
          <img 
            src="https://images.unsplash.com/photo-1601655856683-8400aa9ef59f?w=1920&q=80"
            alt="Abstract green and brown canvas"
            className="w-full h-full object-cover"
            style={{ filter: 'saturate(0.6) brightness(1.1) contrast(0.95) sepia(0.15)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white" />
          <div className="absolute inset-0 bg-gradient-to-tr from-brand-green/8 via-transparent to-brand-brown/12" />
        </div>

        <FallingMapleLeaves />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-brand-green/20 mb-6">
              <Sparkles className="w-4 h-4 text-brand-green" />
              <span className="font-sans text-sm font-semibold text-brand-green uppercase tracking-wider">Insights</span>
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-dark mt-2 mb-6">
              Sustainability <span className="bg-gradient-to-r from-brand-brown to-brand-green bg-clip-text text-transparent">Blog</span>
            </h1>
            <p className="font-sans text-base md:text-lg text-gray-700 leading-relaxed">
              Expert insights on ESG, carbon markets, climate finance, and the latest 
              in sustainability leadership.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b border-gray-100 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between gap-6">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-2 rounded-full font-sans text-sm transition-all ${
                    activeCategory === category
                      ? 'bg-brand-green text-white'
                      : 'bg-brand-surface text-gray-600 hover:bg-brand-green/10'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="blog-search-input"
                className="pl-10 font-sans"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 h-48 rounded-t-2xl"></div>
                  <div className="bg-brand-surface p-6 rounded-b-2xl">
                    <div className="h-4 bg-gray-200 rounded mb-4 w-1/3"></div>
                    <div className="h-6 bg-gray-200 rounded mb-3"></div>
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-sans text-gray-600">No articles found matching your criteria.</p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredPosts.map((post) => (
                <motion.article
                  key={post.id}
                  variants={itemVariants}
                  className="group blog-card bg-brand-surface rounded-2xl overflow-hidden card-hover"
                  data-testid={`blog-post-${post.slug}`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={post.image_url}
                      alt={post.title}
                      className="blog-image w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full font-sans text-xs font-medium text-brand-green">
                        {post.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {/* Meta */}
                    <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span className="font-sans">{formatDate(post.created_at)}</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        <span className="font-sans">{post.author}</span>
                      </span>
                    </div>

                    {/* Title */}
                    <h2 className="font-serif text-xl text-brand-dark mb-3 group-hover:text-brand-green transition-colors line-clamp-2">
                      {post.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="font-sans text-gray-600 text-sm mb-6 line-clamp-3">
                      {post.excerpt}
                    </p>

                    {/* Read More */}
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center font-sans text-sm font-medium text-brand-green hover:gap-3 transition-all"
                    >
                      Read Article
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </main>
  );
};

export default BlogPage;
